import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { es } from './es';
import { en } from './en';

/**
 * Traducciones del panel y del portal. Español por defecto; inglés opcional.
 * El idioma se elige con el selector (se recuerda en el navegador) o con `?lang=en` en la URL,
 * útil para enviar a un cliente extranjero el enlace del portal de activación offline.
 */
export const LANGS = ['es', 'en'] as const;
export type Lang = (typeof LANGS)[number];
export const DEFAULT_LANG: Lang = 'es';
export const LANG_LABEL: Record<Lang, string> = { es: 'Español', en: 'English' };
export const LOCALE: Record<Lang, string> = { es: 'es-ES', en: 'en-GB' };

/** Estructura de las traducciones: la define el español y el inglés debe cumplirla entera. */
type DeepString<T> = { [K in keyof T]: T[K] extends string ? string : DeepString<T[K]> };
export type Dict = DeepString<typeof es>;

type Paths<T, P extends string = ''> = {
  [K in keyof T & string]: T[K] extends string ? `${P}${K}` : Paths<T[K], `${P}${K}.`>;
}[keyof T & string];
export type TKey = Paths<Dict>;
export type TParams = Record<string, string | number>;

const DICTS: Record<Lang, Dict> = { es, en };
const STORAGE_KEY = 'voxel-admin-lang';

const isLang = (v: unknown): v is Lang => typeof v === 'string' && (LANGS as readonly string[]).includes(v);

function initialLang(): Lang {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('lang');
    if (isLang(fromUrl)) {
      localStorage.setItem(STORAGE_KEY, fromUrl);
      return fromUrl;
    }
    const saved = localStorage.getItem(STORAGE_KEY);
    if (isLang(saved)) return saved;
  } catch {
    /* almacenamiento no disponible */
  }
  return DEFAULT_LANG;
}

let current: Lang = DEFAULT_LANG;

function lookup(lang: Lang, key: string): string | undefined {
  let node: unknown = DICTS[lang];
  for (const part of key.split('.')) node = (node as Record<string, unknown> | undefined)?.[part];
  return typeof node === 'string' ? node : undefined;
}

export function format(text: string, params?: TParams): string {
  return params ? text.replace(/\{(\w+)\}/g, (m, k: string) => (k in params ? String(params[k]) : m)) : text;
}

/** Traducción fuera de componentes (utilidades, mensajes de error). Usa el idioma activo. */
export function tr(key: TKey, params?: TParams): string {
  return format(lookup(current, key) ?? lookup(DEFAULT_LANG, key) ?? key, params);
}

/** Traducción de una clave dinámica (p. ej. un código de error de la API); `undefined` si no existe. */
export function trMaybe(key: string, params?: TParams): string | undefined {
  const text = lookup(current, key);
  return text === undefined ? undefined : format(text, params);
}

export const currentLang = () => current;
export const currentLocale = () => LOCALE[current];

interface I18nValue {
  lang: Lang;
  setLang(lang: Lang): void;
  t: typeof tr;
}

const I18nContext = createContext<I18nValue | null>(null);

export function I18nProvider({ children, lang: forced }: { children: ReactNode; lang?: Lang }) {
  const [lang, setLangState] = useState<Lang>(() => forced ?? initialLang());
  current = lang;
  if (typeof document !== 'undefined') document.documentElement.lang = lang;

  const setLang = useCallback((l: Lang) => {
    current = l;
    try {
      localStorage.setItem(STORAGE_KEY, l);
    } catch {
      /* almacenamiento no disponible */
    }
    setLangState(l);
  }, []);

  const value = useMemo<I18nValue>(
    () => ({ lang, setLang, t: (key, params) => format(lookup(lang, key) ?? lookup(DEFAULT_LANG, key) ?? key, params) }),
    [lang, setLang],
  );
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n fuera de I18nProvider');
  return ctx;
}

export const useT = () => useI18n().t;

/** Diccionario activo, para mapas cuyas claves contienen puntos (p. ej. acciones de auditoría). */
export const useDict = (): Dict => DICTS[useI18n().lang];

/** Selector de idioma. */
export function LanguageSwitcher({ className = '' }: { className?: string }) {
  const { lang, setLang, t } = useI18n();
  return (
    <div className={`lang-switch ${className}`} role="group" aria-label={t('common.language')}>
      {LANGS.map((l) => (
        <button key={l} type="button" className={l === lang ? 'active' : ''} aria-pressed={l === lang} onClick={() => setLang(l)} title={LANG_LABEL[l]}>
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
}
